import type { LoginResponse } from '@fieldvision/contracts';
import { api, loginErrorMessage, totpErrorMessage, useAuth } from '@fieldvision/web-core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Banner, Button, Field, Input } from '../components/ui/index.js';
import { APP_VERSION } from '../lib/app-version.js';
import { resolveStaffUrl } from '../runtime-config.js';

/** Optional — when unset the cross-link is simply omitted rather than broken. */
const STAFF_URL = resolveStaffUrl();

/**
 * FR-01 login, console side.
 *
 * Identical protocol to the staff PWA — the NIK, the password, then the optional
 * TOTP step. This used to be one field for email OR phone with the `@` deciding
 * which; that guess is gone along with the two identifiers it was guessing
 * between. The form handling is still duplicated between the two apps rather
 * than shared, because it is a few lines wrapped around a request and extracting
 * it would put a component-shaped thing in a package that deliberately holds no
 * components.
 *
 * There is no sign-up and no password reset here: users are created by an
 * administrator through `POST /auth/users`, and a forgotten password is reset by
 * an administrator through `PUT /auth/users/:id/password`. Someone who still
 * knows theirs changes it from the account menu in the shell.
 */
export function LoginPage() {
  const signIn = useAuth((state) => state.signIn);
  const signedOutReason = useAuth((state) => state.signedOutReason);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');

  /*
   * A reload must land on an empty form, and the browser disagrees.
   *
   * Nothing here persists the NIK or the password — both are `useState('')` and
   * a reload throws that away with the rest of the JS heap. The text that comes
   * back is the password manager filling a saved credential into a form it
   * recognises, with no click and no choice of account, moments after React has
   * rendered both fields empty.
   *
   * That fill is worse than cosmetic, because it does not reach React. State
   * stays `''`, so there is no difference for React to reconcile and the DOM
   * keeps whatever the browser wrote: the fields show an account while
   * `identifier` and `password` are empty, and `disabled` on the submit button
   * below reads those empty strings. The form looks filled in and cannot be
   * submitted — reported as a refresh coming back as somebody else's account.
   *
   * Clearing the fields after mount loses the race; the fill lands afterwards.
   * `readOnly` wins it, because autofill skips a read-only field entirely, and
   * it costs nothing a visitor can feel: the flag is dropped on the pointer
   * press that precedes focus, so the field is already writable by the time the
   * caret arrives and the browser still offers its saved accounts on that same
   * click. Picking one from that list fills through real input events, which
   * React does see — so the deliberate path stays whole while the silent one is
   * closed.
   *
   * `autoComplete` is deliberately left on both inputs. Refusing an uninvited
   * fill is not the same as refusing the credential list, and a login form that
   * fights the password manager teaches people to choose passwords they can
   * retype from memory.
   */
  const [acceptsAutofill, setAcceptsAutofill] = useState(false);
  const armAutofill = () => setAcceptsAutofill(true);

  const login = useMutation({
    mutationFn: () =>
      api.post<LoginResponse | { status: 'TOTP_REQUIRED'; challengeId: string }>('/auth/login', {
        // Upper-cased here as well as on the server. The server's `Nik` contract
        // is the one that counts, but folding before the request means a
        // rejection names what was actually sent.
        identifier: { nik: identifier.trim().toUpperCase() },
        password,
      }),
    /*
     * `inlineError` on both: these two do NOT go through `apiErrorMessage`.
     * `loginErrorMessage` and `totpErrorMessage` turn a status into a sentence
     * that says what to do about it — "NIK atau kata sandi salah." rather than
     * "invalid credentials" — and the toast's generic formatter would report the
     * same failure a second time in worse words.
     */
    meta: { inlineError: true },
    onSuccess: (response) => {
      if ('status' in response) {
        setChallengeId(response.challengeId);
        return;
      }
      signIn(response);
    },
  });

  const totp = useMutation({
    mutationFn: () => api.post<LoginResponse>('/auth/totp', { challengeId, code }),
    meta: { inlineError: true },
    onSuccess: signIn,
  });

  return (
    <main className="flex min-h-dvh items-center justify-center bg-tint p-gutter">
      <div className="surface w-full max-w-md p-6 sm:p-8">
        {/*
          Horizontal lockup: mark and wordmark on one line, the eyebrow stacked
          above the name so the two read as a single unit rather than as an
          image with a caption. `shrink-0` keeps the mark round when the name
          wraps on a narrow phone.
        */}
        <div className="flex items-center gap-3.5">
          <img src="/logo-mark-192.png" alt="" width={48} height={48} className="h-12 w-12 shrink-0" />
          <div className="min-w-0">
            <p className="eyebrow">Konsol Manajer</p>
            <h1 className="mt-0.5 text-2xl leading-tight">
              DIOS <span className="text-gradient">FieldVision</span>
            </h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-muted">Laporan, pantauan lapangan, dan data induk.</p>

        <div className="mt-6 flex flex-col gap-4">
          {/*
            Why this screen is here.

            An expired or revoked token drops the console straight back to the
            login form, and without a word of explanation that is indistinguishable
            from the application having thrown the manager's work away by itself.
            `warn`, not `error`: nothing is broken and nothing was done wrong.

            It stands down as soon as a sign-in is attempted — by then the reason
            they are here is behind them, and stacking it above a "wrong password"
            banner would make the two compete to explain the same form.
          */}
          {signedOutReason === 'EXPIRED' && login.isIdle && totp.isIdle ? (
            <Banner tone="warn">Sesi Anda telah berakhir. Silakan masuk kembali.</Banner>
          ) : null}
          {login.error ? <Banner tone="error" live>{loginErrorMessage(login.error)}</Banner> : null}
          {totp.error ? <Banner tone="error" live>{totpErrorMessage(totp.error)}</Banner> : null}

          {challengeId ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                totp.mutate();
              }}
            >
              <Field label="Kode autentikasi (6 digit)">
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="text-center text-lg tracking-[0.4em]"
                />
              </Field>
              <Button type="submit" variant="primary" size="touch" className="w-full" disabled={totp.isPending || code.length !== 6}>
                {totp.isPending ? 'Memeriksa…' : 'Lanjutkan'}
              </Button>
            </form>
          ) : (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                login.mutate();
              }}
            >
              <Field label="NIK" hint="Nomor Induk Karyawan.">
                <Input
                  autoComplete="username"
                  readOnly={!acceptsAutofill}
                  onPointerDown={armAutofill}
                  onFocus={armAutofill}
                  // Upper-cased as it is typed rather than only on submit, so
                  // the field shows the string that will actually be sent.
                  className="uppercase"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value.toUpperCase())}
                />
              </Field>
              <Field label="Kata sandi">
                <Input
                  type="password"
                  autoComplete="current-password"
                  readOnly={!acceptsAutofill}
                  onPointerDown={armAutofill}
                  onFocus={armAutofill}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
              <Button
                type="submit"
                variant="primary"
                size="touch"
                className="w-full"
                disabled={login.isPending || !identifier || !password}
              >
                {login.isPending ? 'Masuk…' : 'Masuk'}
              </Button>
            </form>
          )}

          {STAFF_URL ? (
            <p className="text-center text-sm text-muted">
              Petugas lapangan?{' '}
              <a className="font-semibold text-primary-700 underline" href={STAFF_URL}>
                Buka aplikasi lapangan
              </a>
            </p>
          ) : null}

          {/*
            The legal documents, from the one screen a signed-out visitor
            reliably reaches.

            Their canonical home is `/legal/*`, which is what goes in the store
            consoles. This is here because someone who has been given the bare
            hostname — a reviewer, an auditor, an employee who was told "it's on
            the manager site" — lands on this form, and a login screen with no
            route to the privacy policy is a dead end for all three.
          */}
          <p className="text-center text-sm text-muted">
            <Link className="focus-ring underline" to="/legal/privacy">
              Kebijakan Privasi
            </Link>
            {' · '}
            <Link className="focus-ring underline" to="/legal/support">
              Dukungan
            </Link>
          </p>

          {/*
            The release number, on the one screen reachable without a session.

            Support asks "what version are you on" before anything else, and
            until now the only answer available from the console was whatever
            the browser had cached — unreadable to the person being asked. The
            signed-in half of that answer is at the foot of the sidebar; this is
            the half for somebody who cannot get past this form, which is
            exactly when the question is most likely to be asked.

            A footnote rather than a card: it is metadata about the application,
            not something to sign in with.
          */}
          <p className="text-center text-xs text-muted">Versi {APP_VERSION}</p>
        </div>
      </div>
    </main>
  );
}
